import React from "react";
import { Box, Container, Typography } from "@mui/material";

const PrivacyPolicy = () => {
  return (
    <Container sx={{ mt: 5, mb: 5 }}>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          padding: 2,
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          paddingX: 4,
          paddingY: 6,
        }}
      >
        {/* Page Title */}
        <Typography
          variant="h3"
          sx={{ fontWeight: "bold", mb: 4, textAlign: "center", color: "#333" }}
        >
          Privacy Policy
        </Typography>

        {/* Introduction */}
        <Typography variant="body1" sx={{ mb: 3 }}>
          This privacy notice for Surobhi Agro Industries Pvt Ltd (doing
          business as Farmology) ('Farmology', 'we', 'us', or 'our'), describes
          how and why we might collect, store, use, and/or share ('process')
          your information when you use our services ('Services'), such as when
          you:
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          - Visit our website at{" "}
          <a href="https://farmology.ai/">Farmology.ai</a> or any website of
          ours that links to this privacy notice.
          <br />- Engage with us in other related ways, including any sales,
          marketing, or events.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>Questions or concerns?</strong> Reading this privacy notice
          will help you understand your privacy rights and choices. If you do
          not agree with our policies and practices, please do not use our
          Services. If you still have any questions or concerns, please contact
          us at farmologyapp@gmail.com.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3, mb: 3 }}>
          Summary of Key Points
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>
            This summary provides key points from our privacy notice, but you
            can find out more details about any of these topics by clicking the
            link following each key point or by using our table of contents
            below to find the section you are looking for.
          </strong>
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>What personal information do we process? </strong>When you
          visit, use, or navigate our Services, we may process personal
          information depending on how you interact with Farmology and the
          Services, the choices you make, and the products and features you use.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>Do we process any sensitive personal information?</strong> We
          do not process sensitive personal information.{" "}
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>Do we receive any information from third parties? </strong>We
          may receive information from public databases, marketing partners,
          social media platforms, and other outside sources.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>How do we process your information? </strong>
          We process your information to provide, improve, and administer our
          Services, communicate with you, for security and fraud prevention, and
          to comply with law. We may also process your information for other
          purposes with your consent. We process your information only when we
          have a valid legal reason to do so.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>
            In what situations and with which parties do we share personal
            information?
          </strong>
          We may share information in specific situations and with specific
          third parties.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>How do we keep your information safe?</strong>
          We have organisational and technical processes and procedures in place
          to protect your personal information. However, no electronic
          transmission over the internet or information storage technology can
          be guaranteed to be 100% secure, so we cannot promise or guarantee
          that hackers, cybercriminals, or other unauthorised third parties will
          not be able to defeat our security and improperly collect, access,
          steal, or modify your information.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>What are your rights? </strong>Depending on where you are
          located geographically, the applicable privacy law may mean you have
          certain rights regarding your personal information.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>How do you exercise your rights?</strong> The easiest way to
          exercise your rights is by filling out our data subject request form
          available here, or by contacting us. We will consider and act upon any
          request in accordance with applicable data protection laws.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          Want to learn more about what Farmology does with any information we
          collect?
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          1. WHAT INFORMATION DO WE COLLECT?
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          Personal information you disclose to us
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>In Short:</strong> We collect personal information that you
          provide to us.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          We collect personal information that you voluntarily provide to us
          when you express an interest in obtaining information about us or our
          products and Services, when you participate in activities on the
          Services, or otherwise when you contact us.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>Personal Information Provided by You.</strong>The personal
          information that we collect depends on the context of your
          interactions with us and the Services, the choices you make, and the
          products and features you use. The personal information we collect may
          include the following:
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <ul>
            <li>Names</li>
            <li>Phone numbers</li>
            <li>email addresses</li>
          </ul>
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>Sensitive Information. </strong>
          We do not process sensitive information.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          All personal information that you provide to us must be true,
          complete, and accurate, and you must notify us of any changes to such
          personal information.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          Personal information you disclose to us
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>In Short: </strong>
          Some information — such as your Internet Protocol (IP) address and/or
          browser and device characteristics — is collected automatically when
          you visit our Services.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          We automatically collect certain information when you visit, use, or
          navigate the Services. This information does not reveal your specific
          identity (like your name or contact information) but may include
          device and usage information, such as your IP address, browser and
          device characteristics, operating system, language preferences,
          referring URLs, device name, country, location, information about how
          and when you use our Services, and other technical information. This
          information is primarily needed to maintain the security and operation
          of our Services, and for our internal analytics and reporting
          purposes.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          Like many businesses, we also collect information through cookies and
          similar technologies.
          <Typography variant="body1" sx={{ mb: 3, mt: 3 }}>
            <span sx={{ mb: 3 }}>
              <strong>The information we collect includes:</strong>
            </span>
            <ul>
              <li>
                Log and Usage Data. Log and usage data is service-related,
                diagnostic, usage, and performance information our servers
                automatically collect when you access or use our Services and
                which we record in log files. Depending on how you interact with
                us, this log data may include your IP address, device
                information, browser type, and settings and information about
                your activity in the Services (such as the date/time stamps
                associated with your usage, pages and files viewed, searches,
                and other actions you take such as which features you use),
                device event information (such as system activity, error reports
                (sometimes called ‘crash dumps’), and hardware settings).
              </li>
              <li>
                Device Data. We collect device data such as information about
                your computer, phone, tablet, or other device you use to access
                the Services. Depending on the device used, this device data may
                include information such as your IP address (or proxy server),
                device and application identification numbers, location, browser
                type, hardware model, Internet service provider and/or mobile
                carrier, operating system, and system configuration information.
              </li>
              <li>
                Location Data. We collect location data such as information
                about your device’s location, which can be either precise or
                imprecise. How much information we collect depends on the type
                and settings of the device you use to access the Services. For
                example, we may use GPS and other technologies to collect
                geolocation data that tells us your current location (based on
                your IP address). You can opt out of allowing us to collect this
                information either by refusing access to the information or by
                disabling your Location setting on your device. However, if you
                choose to opt out, you may not be able to use certain aspects of
                the Services.
              </li>
            </ul>
          </Typography>
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong> Information collected from other sources</strong>
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>In Short: </strong>We may collect limited data from public
          databases, marketing partners, and other outside sources.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          2. HOW DO WE PROCESS YOUR INFORMATION?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>In Short: </strong>We process your information to provide,
          improve, and administer our Services, communicate with you, for
          security and fraud prevention, and to comply with law. We may also
          process your information for other purposes with your consent.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>
            We process your personal information for a variety of reasons,
            depending on how you interact with our Services, including:
          </strong>
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <ul>
            <li>
              <strong>To request feedback. </strong>We may process your
              information when necessary to request feedback and to contact you
              about your use of our Services.
            </li>
            <li>
              <strong>
                To send you marketing and promotional communications.
              </strong>
              We may process the personal information you send to us for our
              marketing purposes, if this is in accordance with your marketing
              preferences. You can opt out of our marketing emails at any time.
              For more information, see ‘WHAT ARE YOUR PRIVACY RIGHTS?‘ below.
            </li>
            <li>
              <strong>To deliver targeted advertising to you.</strong> We may
              process your information to develop and display personalised
              content and advertising tailored to your interests, location, and
              more.
            </li>
            <li>
              <strong>To protect our Services. </strong>We may process your
              information as part of our efforts to keep our Services safe and
              secure, including fraud monitoring and prevention.
            </li>
            <li>
              <strong>To identify usage trends. </strong> We may process
              information about how you use our Services to better understand
              how they are being used so we can improve them.
            </li>
            <li>
              <strong>
                To determine the effectiveness of our marketing and promotional
                campaigns.
              </strong>
              We may process your information to better understand how to
              provide marketing and promotional campaigns that are most relevant
              to you.
            </li>
            <li>
              <strong>
                To save or protect an individual’s vital interest.
              </strong>
              We may process your information when necessary to save or protect
              an individual’s vital interest, such as to prevent harm.
            </li>
          </ul>
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>In Short: </strong>We only process your personal information
          when we believe it is necessary and we have a valid legal reason (i.e.
          legal basis) to do so under applicable law, like with your consent, to
          comply with laws, to provide you with services to enter into or fulfil
          our contractual obligations, to protect your rights, or to fulfil our
          legitimate business interests.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>
            If you are located in the EU or UK, this section applies to you.
          </strong>
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          The General Data Protection Regulation (GDPR) and UK GDPR require us
          to explain the valid legal bases we rely on in order to process your
          personal information. As such, we may rely on the following legal
          bases to process your personal information:
          <ul>
            <li>
              <strong>Consent</strong>
              We may process your information if you have given us permission
              (i.e. consent) to use your personal information for a specific
              purpose. You can withdraw your consent at any time.
            </li>
            <li>
              <strong>Legitimate Interests.</strong>
              We may process your information when we believe it is reasonably
              necessary to achieve our legitimate business interests and those
              interests do not outweigh your interests and fundamental rights
              and freedoms. For example, we may process your personal
              information for some of the purposes described in order to:
              <ul>
                <li>
                  Send users information about special offers and discounts on
                  our products and services
                </li>
                <li>
                  Develop and display personalised and relevant advertising
                  content for our users Develop and display personalised and
                  relevant advertising content for our users
                </li>
                <li>
                  Analyse how our services are used so we can improve them to
                  engage and retain users
                </li>
                <li>Support our marketing activities</li>
                <li>Diagnose problems and/or prevent fraudulent activities</li>
                <li>
                  Understand how our users use our products and services so we
                  can improve user experience
                </li>
              </ul>
            </li>
            <li>
              <strong>Legal Obligations. </strong>
              We may process your information where we believe it is necessary
              for compliance with our legal obligations, such as to cooperate
              with a law enforcement body or regulatory agency, exercise or
              defend our legal rights, or disclose your information as evidence
              in litigation in which we are involved.
            </li>
            <li>
              <strong>Vital Interests.</strong>
              We may process your information where we believe it is necessary
              to protect your vital interests or the vital interests of a third
              party, such as situations involving potential threats to the
              safety of any person.
            </li>
          </ul>
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>
            If you are located in Canada, this section applies to you.
          </strong>
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          We may process your information if you have given us specific
          permission (i.e. express consent) to use your personal information for
          a specific purpose, or in situations where your permission can be
          inferred (i.e. implied consent). You can withdraw your consent at any
          time.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          In some exceptional cases, we may be legally permitted under
          applicable law to process your information without your consent,
          including, for example:
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <ul>
            <li>
              If collection is clearly in the interests of an individual and
              consent cannot be obtained in a timely way
            </li>
            <li>For investigations and fraud detection and prevention</li>
            <li>
              For business transactions provided certain conditions are met
            </li>
            <li>
              If it is contained in a witness statement and the collection is
              necessary to assess, process, or settle an insurance claim
            </li>
            <li>
              For identifying injured, ill, or deceased persons and
              communicating with next of kin
            </li>
            <li>
              If we have reasonable grounds to believe an individual has been,
              is, or may be victim of financial abuse
            </li>
            <li>
              If it is reasonable to expect collection and use with consent
              would compromise the availability or the accuracy of the
              information and the collection is reasonable for purposes related
              to investigating a breach of an agreement or a contravention of
              the laws of Canada or a province
            </li>
            <li>
              If disclosure is required to comply with a subpoena, warrant,
              court order, or rules of the court relating to the production of
              records
            </li>
            <li>
              If it was produced by an individual in the course of their
              employment, business, or profession and the collection is
              consistent with the purposes for which the information was
              produced
            </li>
            <li>
              If the collection is solely for journalistic, artistic, or
              literary purposes
            </li>
            <li>
              If the information is publicly available and is specified by the
              regulations
            </li>
          </ul>
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>In Short:</strong> We may share information in specific
          situations described in this section and/or with the following third
          parties.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>
            Vendors, Consultants, and Other Third-Party Service Providers.
          </strong>
          We may share your data with third-party vendors, service providers,
          contractors, or agents (‘third parties‘) who perform services for us
          or on our behalf and require access to such information to do that
          work. The third parties we may share personal information with are as
          follows:
          <ul>
            <li>
              <strong>
                Advertising, Direct Marketing, and Lead Generation
              </strong>
            </li>
          </ul>
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          Google AdSense
          <ul>
            <li>
              <strong>Cloud Computing Services</strong>
            </li>
          </ul>
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Google Cloud Platform
          <ul>
            <li>
              <strong>Website Hosting</strong>
            </li>
          </ul>
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          WordPress.com
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          We also may need to share your personal information in the following
          situations:
          <ul>
            <li>
              <strong>Business Transfers. </strong>We may share or transfer your
              information in connection with, or during negotiations of, any
              merger, sale of company assets, financing, or acquisition of all
              or a portion of our business to another company.
            </li>
            <li>
              <strong>When we use Google Maps Platform APIs.</strong>
              We may share your information with certain Google Maps Platform
              APIs (e.g. Google Maps API, Places API). To find out more about
              Google’s Privacy Policy, please refer to this link.
            </li>
            <li>
              <strong>Affiliates. </strong>We may share your information with
              our affiliates, in which case we will require those affiliates to
              honour this privacy notice. Affiliates include our parent company
              and any subsidiaries, joint venture partners, or other companies
              that we control or that are under common control with us.
            </li>
          </ul>
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>In Short:</strong> We may use cookies and other tracking
          technologies to collect and store your information.
        </Typography>

        <Typography variant="body1" sx={{  mt: 3 }}>
          We may use cookies and similar tracking technologies (like web beacons
          and pixels) to access or store information. Specific information about
          how we use such technologies and how you can refuse certain cookies is
          set out in our Cookie Notice.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          6. HOW LONG DO WE KEEP YOUR INFORMATION?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>In Short:</strong>We keep your information for as long as
          necessary to fulfil the purposes outlined in this privacy notice
          unless otherwise required by law.
        </Typography>

        <Typography variant="body1" sx={{  mt: 3 }}>
          We will only keep your personal information for as long as it is
          necessary for the purposes set out in this privacy notice, unless a
          longer retention period is required or permitted by law (such as tax,
          accounting, or other legal requirements). No purpose in this notice
          will require us keeping your personal information for longer than 2
          years.
        </Typography>
        <Typography variant="body1" sx={{  mt: 3 }}>
          When we have no ongoing legitimate business need to process your
          personal information, we will either delete or anonymise such
          information, or, if this is not possible (for example, because your
          personal information has been stored in backup archives), then we will
          securely store your personal information and isolate it from any
          further processing until deletion is possible.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          7. HOW DO WE KEEP YOUR INFORMATION SAFE?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>In Short:</strong>We aim to protect your personal information
          through a system of organisational and technical security measures.
        </Typography>

        <Typography variant="body1" sx={{ mt: 3 }}>
          We have implemented appropriate and reasonable technical and
          organisational security measures designed to protect the security of
          any personal information we process. However, despite our safeguards
          and efforts to secure your information, no electronic transmission
          over the Internet or information storage technology can be guaranteed
          to be 100% secure, so we cannot promise or guarantee that hackers,
          cybercriminals, or other unauthorised third parties will not be able
          to defeat our security and improperly collect, access, steal, or
          modify your information. Although we will do our best to protect your
          personal information, transmission of personal information to and from
          our Services is at your own risk. You should only access the Services
          within a secure environment.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          8. WHAT ARE YOUR PRIVACY RIGHTS?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          In some regions, such as the European Economic Area (EEA), United
          Kingdom (UK), and Canada, you have rights that allow you greater
          access to and control over your personal information. You may review,
          change, or terminate your account at any time.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          In some regions (like the EEA, UK, and Canada), you have certain
          rights under applicable data protection laws. These may include the
          right (i) to request access and obtain a copy of your personal
          information, (ii) to request rectification or erasure; (iii) to
          restrict the processing of your personal information; and (iv) if
          applicable, to data portability. In certain circumstances, you may
          also have the right to object to the processing of your personal
          information. You can make such a request by contacting us by using the
          contact details provided in the section ‘HOW CAN YOU CONTACT US ABOUT
          THIS NOTICE?‘ below.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          We will consider and act upon any request in accordance with
          applicable data protection laws.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          If you are located in the EEA or UK and you believe we are unlawfully
          processing your personal information, you also have the right to
          complain to your local data protection supervisory authority. You can
          find their contact details here:
          https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>Withdrawing your consent:</strong>
          If we are relying on your consent to process your personal
          information, which may be express and/or implied consent depending on
          the applicable law, you have the right to withdraw your consent at any
          time. You can withdraw your consent at any time by contacting us by
          using the contact details provided in the section ‘HOW CAN YOU CONTACT
          US ABOUT THIS NOTICE?‘ below.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          However, please note that this will not affect the lawfulness of the
          processing before its withdrawal, nor when applicable law allows, will
          it affect the processing of your personal information conducted in
          reliance on lawful processing grounds other than consent.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>Cookies and similar technologies:</strong>
          Most Web browsers are set to accept cookies by default. If you prefer,
          you can usually choose to set your browser to remove cookies and to
          reject cookies. If you choose to remove cookies or reject cookies,
          this could affect certain features or services of our Services. To opt
          out of interest-based advertising by advertisers on our Services visit
          http://www.aboutads.info/choices/.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          9. CONTROLS FOR DO-NOT-TRACK FEATURES
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Most web browsers and some mobile operating systems and mobile
          applications include a Do-Not-Track (‘DNT’) feature or setting you can
          activate to signal your privacy preference not to have data about your
          online browsing activities monitored and collected. At this stage no
          uniform technology standard for recognising and implementing DNT
          signals has been finalised. As such, we do not currently respond to
          DNT browser signals or any other mechanism that automatically
          communicates your choice not to be tracked online. If a standard for
          online tracking is adopted that we must follow in the future, we will
          inform you about that practice in a revised version of this privacy
          notice.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          10. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>In Short: </strong>Yes, if you are a resident of California,
          you are granted specific rights regarding access to your personal
          information.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          California Civil Code Section 1798.83, also known as the ‘Shine The
          Light’ law, permits our users who are California residents to request
          and obtain from us, once a year and free of charge, information about
          categories of personal information (if any) we disclosed to third
          parties for direct marketing purposes and the names and addresses of
          all third parties with which we shared personal information in the
          immediately preceding calendar year. If you are a California resident
          and would like to make such a request, please submit your request in
          writing to us using the contact information provided below.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          If you are under 18 years of age, reside in California, and have a
          registered account with Services, you have the right to request
          removal of unwanted data that you publicly post on the Services. To
          request removal of such data, please contact us using the contact
          information provided below and include the email address associated
          with your account and a statement that you reside in California. We
          will make sure the data is not publicly displayed on the Services, but
          please be aware that the data may not be completely or comprehensively
          removed from all our systems (e.g. backups, etc.).
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          11. DO WE MAKE UPDATES TO THIS NOTICE?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>In Short: </strong>Yes, we will update this notice as
          necessary to stay compliant with relevant laws.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          We may update this privacy notice from time to time. The updated
          version will be indicated by an updated ‘Revised’ date and the updated
          version will be effective as soon as it is accessible. If we make
          material changes to this privacy notice, we may notify you either by
          prominently posting a notice of such changes or by directly sending
          you a notification. We encourage you to review this privacy notice
          frequently to be informed of how we are protecting your information.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          If you have questions or comments about this notice, you may email us
          at{" "}
          <a href="mailto:info@farmologyindia.com">info@farmologyindia.com</a>{" "}
          or by post to:
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          Surobhi Agro Industries Pvt Ltd
          <br />
          KASA Industrial Park, Duila
          <br />
          Andul road
          <br />
          Howrah, West Bengal 711302
          <br />
          India
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3 }}>
          13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
          YOU?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Based on the applicable laws of your country, you may have the right
          to request access to the personal information we collect from you,
          change that information, or delete it in some circumstances. To
          request to review, update, or delete your personal information, please
          submit a request form by clicking here. We will respond to your
          request within 30 days.
        </Typography>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
